import { classroomsApi } from "@api/classrooms"
import type { GradesExportScope, UpdateClassroomPayload } from "@api/classrooms"
import { subjectsApi } from "@api/subjects"
import { homeKeys } from "@features/home"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { classroomKeys, subjectKeys } from "./queryKeys"

import type { Classroom } from "@/types"

// PRD-45 §FR-2.3 — `includeArchived` defaults to false (active-only,
// matching the backend's own default), which is what every existing
// caller (sidebar, material-request picker, gradebook/scan-and-grade
// auto-restore, onboarding-gate count) wants without passing anything.
// Only the /salones "Archivados" tab passes `{ includeArchived: true }`.
export const useClassrooms = (opts?: { includeArchived?: boolean }) =>
  useQuery({
    queryKey: opts?.includeArchived ? classroomKeys.listIncludingArchived() : classroomKeys.lists(),
    queryFn: () => classroomsApi.list(opts),
  })

// PRD-37 / ADR-0031 — the professor's school-scoped subject catalog.
// Bounded inventory, no params, no pagination (pagination.md's exemption).
export const useSubjects = () =>
  useQuery({
    queryKey: subjectKeys.lists(),
    queryFn: subjectsApi.list,
  })

export const useClassroom = (id: string | undefined) =>
  useQuery({
    queryKey: classroomKeys.detail(id ?? ""),
    queryFn: () => classroomsApi.get(id as string),
    enabled: !!id,
  })

/**
 * PRD-61 §FR-3 — the roster's Resultados section.
 *
 * No `placeholderData` on purpose: a refetch of the SAME key already keeps its
 * data on screen, so the placeholder would only ever act when the key
 * changes — i.e. when the sidebar moves her to another classroom, where it
 * would show classroom A's cuts, averages and download button under B's name.
 */
export const useClassroomResults = (id: string | undefined) =>
  useQuery({
    queryKey: classroomKeys.results(id ?? ""),
    queryFn: () => classroomsApi.results(id as string),
    enabled: !!id,
  })

/**
 * PRD-61 §FR-4 — the kardex CSV. A mutation rather than a query: it is an
 * action the professor takes, never cached, and it carries its own
 * pending/error state for the button. The page saves the blob and toasts.
 */
export const useDownloadGradesExport = () =>
  useMutation({
    mutationFn: ({
      id,
      scope,
    }: {
      id: string
      scope: GradesExportScope
      /** The classroom's student count, for analytics only. */
      studentCount: number
    }) => classroomsApi.gradesExport(id, scope),
    onSuccess: (_file, { studentCount }) => {
      // `rows` is an ESTIMATE: the file carries one row per active student
      // (§FR-4.2), and the browser cannot count CSV rows without parsing the
      // body, so it reports the classroom's student count instead. The exact
      // figure is in the server's `classroom.exported` audit row.
      analytics.track("export_downloaded", { kind: "grades_csv", rows: studentCount })
    },
  })

// PRD-10 §FR-32: every classroom mutation also refreshes the home dashboard's
// aggregates so the next dashboard view reflects the change.
const invalidateClassroomCaches = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: classroomKeys.all })
  qc.invalidateQueries({ queryKey: homeKeys.all })
}

export const useCreateClassroom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: classroomsApi.create,
    onSuccess: (classroom) => {
      analytics.track("classroom_created", { classroom_id: classroom.id })
      invalidateClassroomCaches(qc)
    },
  })
}

// There is no hard delete. PRD-45 replaced it with archive/restore — a
// classroom owns its roster, its materials and every grade in them, so
// "reversible declutter" is the only gesture the product offers. The hook and
// the `classroomsApi.delete` client that used to exist here had no call site
// and no UI; they were a loaded gun pointed at a semester of work (PRD-59).

// PRD-45 — reversible declutter. A plain invalidate (not the in-place
// `setQueryData` patch `useUpdateClassroom` uses below) is correct here:
// archiving/restoring a row must move it between the "Activos" and
// "Archivados" query caches, which an in-place patch of one list can't do.
export const useArchiveClassroom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: classroomsApi.archive,
    onSuccess: (_result, id) => {
      analytics.track("classroom_archived", { classroom_id: id })
      invalidateClassroomCaches(qc)
    },
  })
}

export const useRestoreClassroom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: classroomsApi.restore,
    onSuccess: (_result, id) => {
      analytics.track("classroom_restored", { classroom_id: id })
      invalidateClassroomCaches(qc)
    },
  })
}

export const useUpdateClassroom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClassroomPayload }) =>
      classroomsApi.update(id, data),
    onSuccess: (updated, { id }) => {
      analytics.track("classroom_updated", { classroom_id: id })
      // Patch in-place so the list order stays stable during the session.
      // A full invalidate would re-sort by classroom_alias and visually jump
      // the card to its new alphabetical position mid-edit.
      qc.setQueryData<Classroom[]>(classroomKeys.lists(), (prev) =>
        prev?.map((c) => (c.id === id ? updated : c)),
      )
      qc.setQueryData(classroomKeys.detail(id), updated)
      // Home aggregates don't depend on alias order, so a plain invalidate is fine.
      qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}
